import React from 'react';
// import React, { useState } from 'react'; // Ancienne version si besoin de state local
import { useParams, Link } from 'react-router-dom';
// import { useParams, useNavigate, Link } from 'react-router-dom'; // Ancienne version avec navigate
import { useQuery } from 'react-query';
import {
  ArrowLeftIcon,
  PencilIcon,
  UserIcon,
  BriefcaseIcon,
  HomeIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  MapPinIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import { mutualisteAPI } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const AdminMutualisteDetail = () => {
  const { id } = useParams();
  // const navigate = useNavigate(); // Conservé en commentaire pour usage futur éventuel

  const { data: mutualisteData, isLoading, error } = useQuery(
    ['mutualiste', id],
    () => mutualisteAPI.getMutualiste(id),
    {
      onError: (error) => {
        console.error('Erreur récupération mutualiste:', error);
        toast.error('Erreur lors du chargement des informations');
      }
    }
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !mutualisteData?.data) {
    return (
      <div className="bg-white rounded-xl shadow-card p-12 text-center">
        <p className="text-red-500">Erreur lors du chargement des informations</p>
        <Link
          to="/admin/mutualistes"
          className="mt-4 inline-flex items-center text-primary-600 hover:text-primary-900"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Retour à la liste
        </Link>
      </div>
    );
  }

  const mutualiste = mutualisteData.data;

  const getSafeLabel = (value) => {
    if (!value) return 'N/A';

    // Valeurs simples
    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }

    // Objets avec libelle potentiellement imbriqué
    if (typeof value === 'object') {
      const lib = value.libelle;

      if (typeof lib === 'string' || typeof lib === 'number') {
        return lib;
      }

      if (lib && typeof lib === 'object') {
        const nestedLib = lib.libelle;
        if (typeof nestedLib === 'string' || typeof nestedLib === 'number') {
          return nestedLib;
        }
      }

      return 'N/A';
    }

    // Fallback ultime en chaîne
    return String(value);
  };

  const getStatusBadge = (statusObj) => {
    // Gérer le cas où status est un objet ou une chaîne
    const rawLabel = statusObj?.libelle ?? statusObj;
    const statusLabel = typeof rawLabel === 'object' ? (rawLabel?.libelle || 'N/A') : (rawLabel || 'N/A');

    // Normaliser le libellé pour la comparaison (enlever les variations)
    const normalizedLabel = statusLabel.toLowerCase().replace(/[()]/g, '').trim();
    
    // Utiliser le libellé normalisé pour déterminer les couleurs
    let config = { bg: 'bg-gray-100', text: 'text-gray-800' }; // Par défaut
    
    if (normalizedLabel.includes('actif')) {
      config = { bg: 'bg-green-100', text: 'text-green-800' };
    } else if (normalizedLabel.includes('suspendu')) {
      config = { bg: 'bg-yellow-100', text: 'text-yellow-800' };
    } else if (normalizedLabel.includes('radié') || normalizedLabel.includes('radie')) {
      config = { bg: 'bg-red-100', text: 'text-red-800' };
    }
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {typeof statusLabel === 'object' ? (statusLabel?.libelle || 'N/A') : statusLabel}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const calculateAge = (dateNaissance) => {
    if (!dateNaissance) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateNaissance);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return `${age} ans`;
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/admin/mutualistes"
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Retour à la liste
          </Link>
        </div>
        <Link
          to={`/admin/mutualistes/${id}/modifier`}
          className="btn-primary flex items-center"
        >
          <PencilIcon className="h-5 w-5 mr-2" />
          Modifier
        </Link>
      </div>

      {/* Carte principale */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center">
            <div className="h-20 w-20 rounded-full bg-primary-100 flex items-center justify-center mr-4">
              <span className="text-2xl font-bold text-primary-600">
                {mutualiste.nom?.charAt(0)}{mutualiste.prenoms?.charAt(0)}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {mutualiste.nom} {mutualiste.prenoms}
              </h1>
              <p className="text-gray-600 mt-1">
                {mutualiste.type === 'ADHERENT' ? 'Adhérent' : 'Ayant-droit'}
              </p>
              <div className="flex items-center space-x-2 mt-2">
                <span className="text-sm font-mono text-gray-500">
                  Matricule: {mutualiste.matricule}
                </span>
              </div>
            </div>
          </div>
          <div>
            {getStatusBadge(mutualiste.status)}
          </div>
        </div>

        {/* Informations personnelles */}
        <div className="border-t pt-6">
          <div className="flex items-center mb-4">
            <UserIcon className="h-6 w-6 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              Informations personnelles
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Genre</label>
              <p className="text-gray-900">
                {getSafeLabel(mutualiste.genre)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Date de naissance</label>
              <p className="text-gray-900 flex items-center">
                <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" />
                {formatDate(mutualiste.dateNaissance)} ({calculateAge(mutualiste.dateNaissance)})
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Lieu de naissance</label>
              <p className="text-gray-900">{mutualiste.lieuNaissance || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Nationalité</label>
              <p className="text-gray-900">{getSafeLabel(mutualiste.nationalite)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Situation matrimoniale</label>
              <p className="text-gray-900">
                {getSafeLabel(mutualiste.situationMatrimoniale)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Date d'inscription</label>
              <p className="text-gray-900">{formatDate(mutualiste.createdAt)}</p>
            </div>
          </div>
        </div>

        {/* Informations professionnelles */}
        {mutualiste.type === 'ADHERENT' && (
          <div className="border-t pt-6 mt-6">
            <div className="flex items-center mb-4">
              <BriefcaseIcon className="h-6 w-6 text-primary-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">
                Informations professionnelles
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Entreprise</label>
                <p className="text-gray-900">
                  {mutualiste.enterprise?.nom || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Fonction</label>
                <p className="text-gray-900">
                  {mutualiste.fonction?.libelle || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Email professionnel</label>
                <p className="text-gray-900 flex items-center">
                  {mutualiste.emailProfessionnel ? (
                    <>
                      <EnvelopeIcon className="h-4 w-4 mr-1 text-gray-400" />
                      {mutualiste.emailProfessionnel}
                    </>
                  ) : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Téléphone professionnel</label>
                <p className="text-gray-900 flex items-center">
                  {mutualiste.telephoneProfessionnel ? (
                    <>
                      <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                      {mutualiste.telephoneProfessionnel}
                    </>
                  ) : 'N/A'}
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-500">Adresse professionnelle</label>
                <p className="text-gray-900">
                  {mutualiste.adresseProfessionnelle || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Informations de résidence */}
        <div className="border-t pt-6 mt-6">
          <div className="flex items-center mb-4">
            <HomeIcon className="h-6 w-6 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              Informations de résidence
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-500">Adresse de résidence</label>
              <p className="text-gray-900 flex items-center">
                <MapPinIcon className="h-4 w-4 mr-1 text-gray-400" />
                {mutualiste.adresseResidence || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Ville</label>
              <p className="text-gray-900">{mutualiste.ville || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Code postal</label>
              <p className="text-gray-900">{mutualiste.codePostal || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Pays</label>
              <p className="text-gray-900">{mutualiste.pays || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Adresse postale</label>
              <p className="text-gray-900">{mutualiste.adressePostale || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Informations de contact */}
        <div className="border-t pt-6 mt-6">
          <div className="flex items-center mb-4">
            <PhoneIcon className="h-6 w-6 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              Informations de contact
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Mobile 1</label>
              <p className="text-gray-900 flex items-center">
                <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                {mutualiste.mobile1 || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Mobile 2</label>
              <p className="text-gray-900 flex items-center">
                {mutualiste.mobile2 ? (
                  <>
                    <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                    {mutualiste.mobile2}
                  </>
                ) : 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email personnel</label>
              <p className="text-gray-900 flex items-center">
                {mutualiste.emailPersonnel ? (
                  <>
                    <EnvelopeIcon className="h-4 w-4 mr-1 text-gray-400" />
                    {mutualiste.emailPersonnel}
                  </>
                ) : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Informations sur le compte utilisateur */}
        {mutualiste.user && (
          <div className="border-t pt-6 mt-6">
            <div className="flex items-center mb-4">
              <IdentificationIcon className="h-6 w-6 text-primary-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">
                Compte utilisateur
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Email de connexion</label>
                <p className="text-gray-900">{mutualiste.user.email || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Rôle</label>
                <p className="text-gray-900">{mutualiste.user.role || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Statut du compte</label>
                <div className="mt-1">
                  {getStatusBadge(mutualiste.user.status)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMutualisteDetail;
